import { TERMINAL_WORK_ITEM_STATUSES } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ListTodo, Plus } from 'lucide-react';
import { useState } from 'react';
import { RelativeTime } from '@/components/relative-time';
import { WorkItemStatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/**
 * VICKI's tasks (docs/features/front-desk.md#tasks): Work Items of the Front Desk she works through in the background —
 * given by people here, by a desk that hands work up, or by herself. Each opens as a Work Item.
 */
export function VickiTasks({ accountId }: { accountId: string }) {
  const tasks = useQuery({
    queryKey: qk.frontDeskTasks(accountId),
    queryFn: () => api.frontDesk.tasks(accountId),
    refetchInterval: (q) => (q.state.data?.some((w) => !TERMINAL_WORK_ITEM_STATUSES.includes(w.status)) ? 10_000 : false),
  });
  const [adding, setAdding] = useState(false);
  const list = tasks.data ?? [];
  const open = list.filter((w) => !TERMINAL_WORK_ITEM_STATUSES.includes(w.status));
  const done = list.filter((w) => TERMINAL_WORK_ITEM_STATUSES.includes(w.status));
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <ListTodo className="size-4 text-primary" />
        <h2 className="flex-1 text-sm font-semibold">{t('VICKI’s tasks')}</h2>
        {!adding && (
          <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>
            <Plus /> {t('Task')}
          </Button>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {t('Work VICKI does in the background, longer than a reply: from you, from a desk that hands it up, or from herself.')}
      </p>
      {adding && <TaskForm accountId={accountId} onDone={() => setAdding(false)} />}
      {!tasks.isPending && !open.length && !adding && (
        <p className="mt-3 rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">{t('No open tasks.')}</p>
      )}
      <ul className="mt-3 flex flex-col gap-2">
        {open.map((w) => (
          <TaskRow key={w.id} task={w} />
        ))}
      </ul>
      {done.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-muted-foreground">{t('Finished ({n})', { n: done.length })}</summary>
          <ul className="mt-2 flex flex-col gap-2 opacity-75">
            {done.map((w) => (
              <TaskRow key={w.id} task={w} />
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

function TaskRow({ task: w }: { task: Awaited<ReturnType<typeof api.frontDesk.tasks>>[number] }) {
  return (
    <li>
      <Link
        to="/w/$workspaceId/work-items/$workItemId"
        params={{ workspaceId: w.workspaceId, workItemId: w.id }}
        className="flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm hover:bg-muted/50"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{w.title}</span>
          {w.waitingReason && !TERMINAL_WORK_ITEM_STATUSES.includes(w.status) && (
            <span className="block truncate text-xs text-muted-foreground">{w.waitingReason}</span>
          )}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <WorkItemStatusBadge status={w.status} paused={w.paused} />
          <RelativeTime className="text-xs text-muted-foreground" value={w.updatedAt} />
        </span>
      </Link>
    </li>
  );
}

function TaskForm({ accountId, onDone }: { accountId: string; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const add = useApiMutation(() => api.frontDesk.addTask(accountId, { title: title.trim(), description: description.trim() || undefined }), {
    invalidate: [qk.frontDeskTasks(accountId)],
    success: t('VICKI starts on it.'),
    onSuccess: onDone,
  });
  return (
    <form
      className="mt-3 flex flex-col gap-2 rounded-lg border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) add.mutate();
      }}
    >
      <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('What should VICKI do?')} maxLength={300} />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t('What is wanted, what counts as done, what she must not do (optional)')}
        rows={3}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          {t('Cancel')}
        </Button>
        <Button type="submit" size="sm" disabled={!title.trim()} loading={add.isPending}>
          {t('Give to VICKI')}
        </Button>
      </div>
    </form>
  );
}
